import { selectUserInfo } from "features/user/userSlice";
import { Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ClientProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Client } from "assets/SideBarIcons/customer.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { pickClientDisplayName } from "Helpers/pickProfileEntityTitle";
import { ChangeEvent, useEffect, useState } from "react";
import Loader from "layout/Loader";
import CustomerTabs from "./CustomerTabs";
import CommonCheckbox from "common/CheckBox/Checkbox";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  ClientRequest,
  GetClientByIdResponse,
} from "subModule/src/services/client/interface";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import FormDialog from "common/Modal/Modal";
import DynamicSelect from "common/Select/Select";

interface FormData {
  [key: string]: any;
}
interface CustomerProps {
  setAddNew?: any;
  customersRefetch?: any;
}
export default function CustomersModal(props: CustomerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "customers",
    moduleLabelKey: "customersList",
    moduleHref: "/clients",
    entityFallbackLabelKey: "name",
    isEdit: !!params?.id && !props?.setAddNew,
  });
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedCustomerNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phCustomerNameAr") : t("phCustomerNameEn");
  const [loading, setLoading] = useState<boolean>(
    params?.id && !props?.setAddNew ? true : false,
  );
  const [username, setUsername] = useState<string>("");
  const [customer, setCustomer] = useState<any>({});
  const [updateModal, setUpdateModal] = useState<boolean>(false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id && !props?.setAddNew ? false : true,
  );
  const sourceList = [
    { label: t("Walk-in"), value: 1 },
    { label: t("Phone / WhatsApp"), value: 2 },
    { label: t("Online"), value: 3 },
    { label: t("Referral"), value: 4 },
    { label: t("Insurance"), value: 5 },
    { label: t("Corporate / Fleet"), value: 6 },
    { label: t("Returning Customer"), value: 7 },
    { label: t("OTHER"), value: 8 },
  ];
  useEffect(() => {
    if (params?.id && !props?.setAddNew) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    ClientProfileService.getClientByID(+(params as any)?.id, "customers")
      .then((res: GetClientByIdResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            username:
              res?.data?.autoDetails === 2 ||
              res?.data?.autoDetails === 10 ||
              res?.data?.autoDetails === 11 ||
              res?.data?.autoDetails === 12 ||
              res?.data?.autoDetails === 20 ||
              res?.data?.autoDetails === 21
                ? ""
                : res?.data?.username,
            source:
              res?.data?.source && res?.data?.source !== "0"
                ? {
                    value: Number(res?.data?.source) || "",
                    label:
                      sourceList.find(
                        (v) => v.value === Number(res?.data?.source),
                      )?.label || res?.data?.source,
                  }
                : null,
          });
          setUsername(res?.data?.username || "");
          captureLabel(pickClientDisplayName(res));
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id && !props?.setAddNew) {
      const existingLoginEmail = data?.username || username || null;
      const updatePayload: any = {
        ...data,
        email: existingLoginEmail,
        username: existingLoginEmail,
        loginAccess: Boolean(data?.loginAccess),
        source: data?.source?.value || data?.source || null,
      };
      if (!data?.loginAccess) {
        updatePayload.email = null;
        updatePayload.username = null;
        delete updatePayload.password;
        delete updatePayload.confirmPassword;
        delete updatePayload.showPassword;
        delete updatePayload.showConfirmPassword;
      }
      setCustomer(updatePayload);
      setUpdateModal(true);
    } else {
      delete data.showConfirmPassword;
      delete data.showPassword;
      delete data.confirmPassword;
      ClientProfileService.createClient(
        {
          ...data,
          type: 170,
          email: data?.loginAccess ? data.username : null,
          username: data?.loginAccess ? data.username : null,
          loginAccess: Boolean(data?.loginAccess),
          active: true,
          business: {},
          source: data?.source?.value || data?.source || null,
        },
        "customers",
      )
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("customer") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            if (props?.setAddNew) {
              props?.setAddNew(false);
              props.customersRefetch();
            } else navigate("/clients");
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const updateCustomer = (payload: ClientRequest) => {
    ClientProfileService.updateClient(
      +(params as any)?.id,
      payload,
      "customers",
    )
      .then((response: any) => {
        if (response.success) {
          captureLabel(
            (payload as any)?.name || pickClientDisplayName(response),
          );
          getByID();
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                t("customer") + " " + t("updated") + " " + t("successfully")
              }
            />,
            { variant: "success" },
          );
        } else {
          enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setIsBtnEnabled(true));
  };
  return (
    <>
      {props?.setAddNew ? null : (
        <ComponentHeaderTwo
          breadcrumbItems={breadcrumbItems}
          showBtn={true}
          showSaveBtn={true}
          onClick={() => navigate("/clients")}
          onSubmit={handleSubmit(onSubmit)}
          heading={
            (params?.id ? t("editCustomer") : t("addCustomer")) +
            " (" +
            t("Individual") +
            ")"
          }
          icon={<Client height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_CUSTOMER_CREATE")}
        />
      )}
      <div className={`${params?.id ? "flex" : ""}`}>
        {params?.id && !props?.setAddNew ? (
          <CustomerTabs params={params} selectedIndex={0} />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={
              (params?.id && !props?.setAddNew
                ? t("editCustomer")
                : t("addCustomer")) +
              " (" +
              t("Individual") +
              ")"
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id && !props?.setAddNew ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            entityPopup={props?.setAddNew}
            padding={!props?.setAddNew}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="items-start mb-2"
                spacing={2}
                onSubmit={handleSubmit(onSubmit)}
              >
                {params?.id &&
                (watch("totalDueAmount") || watch("totalDueAmount") === 0) ? (
                  <Grid item xs={12}>
                    <Typography
                      className="font-semibold font-Saira text-[14px] flex gap-1 justify-end"
                      sx={{ color: themeColors?.primary }}
                    >
                      <span
                        className="font-medium italic"
                        style={{ color: themeColors?.secondary }}
                      >
                        {t("totalDueAmount")}
                      </span>
                      <span style={{ color: themeColors?.secondary }}>:</span>
                      <span className="font-bold">
                        {new Intl.NumberFormat("en", {
                          notation: "standard",
                          maximumFractionDigits: 2,
                        }).format(watch("totalDueAmount") || "")}
                      </span>
                      <span className="font-[400] uppercase">
                        {loggedInUserData?.business?.currency || ""}
                      </span>
                    </Typography>
                  </Grid>
                ) : null}
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("name", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedCustomerNamePlaceholder(
                      primaryLanguage,
                    )}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryName")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedCustomerNamePlaceholder(
                      secondaryLanguage,
                    )}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryName}
                    helperText={errors.secondaryName?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="text"
                    {...register("phone", {
                      validate: {
                        phoneLength: (value) =>
                          value === "" ||
                          value === null ||
                          (value?.length >= 5 && value?.length <= 17) ||
                          t("phoneLength"),
                        // nonNegative: (value) =>
                        //   value === "" ||
                        //   /^\+?[\p{N}]+$/u.test(value) ||
                        //   t("onlyPositiveNumbers"),
                      },
                    })}
                    label={t("phoneNumber")}
                    placeholder={t("phContactPhone")}
                    title={t("phoneNumber")}
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message as string}
                    InputLabelProps={{ shrink: true }}
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                    ) => {
                      const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                      setValue("phone", cleaned);
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("address")}
                    label={t("address")}
                    placeholder={t("phContactAddress")}
                    title={t("address")}
                    fullWidth
                    error={!!errors.address}
                    helperText={errors.address?.message as string}
                    InputLabelProps={{ shrink: true }}
                    multiline
                    rows={1}
                    sx={{
                      "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                        width: "100%",
                        paddingRight: "0px",
                        paddingBottom: 0,
                      },
                      "& .MuiInputBase-inputMultiline": {
                        resize: "vertical",
                        minHeight: "29px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="source"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("source")}
                        {...field}
                        options={sourceList}
                        isSearchable
                        isClearable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors?.source?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} className="mt-[-4px]">
                  <Controller
                    name="loginAccess"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <div className="relative">
                        <CommonCheckbox
                          {...field}
                          label={t("loginAccess")}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            field.onChange(event.target.checked);
                            if (!event.target.checked) {
                              if (!params?.id) {
                                setValue("username", "");
                              }
                              setValue("password", "");
                              setValue("confirmPassword", "");
                            }
                          }}
                          checked={field.value}
                          color={themeColors?.primary}
                        />
                      </div>
                    )}
                  />
                </Grid>
                {watch("loginAccess") ? (
                  <>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="email"
                        {...register("username", {
                          required: watch("loginAccess")
                            ? t("This field is required.")
                            : false,
                          pattern: {
                            value:
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: t("validEmail"),
                          },
                        })}
                        label={t("email") + (watch("loginAccess") ? " *" : "")}
                        placeholder={t("phUserEmail")}
                        title={t("email") + (watch("loginAccess") ? " *" : "")}
                        fullWidth
                        error={!!errors.username}
                        helperText={errors.username?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    {params?.id && !props?.setAddNew ? null : (
                      <>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type={watch("showPassword") ? "text" : "password"}
                            {...register("password", {
                              required: watch("loginAccess")
                                ? t("This field is required.")
                                : false,
                              validate:
                                watch("loginAccess") ||
                                watch("password")?.length
                                  ? {
                                      passwordLength: (value) =>
                                        value?.length >= 8 ||
                                        t("minPasswordLength"),
                                    }
                                  : undefined,
                            })}
                            label={
                              t("password") + (watch("loginAccess") ? " *" : "")
                            }
                            placeholder={t("phEnterPassword")}
                            title={
                              t("password") + (watch("loginAccess") ? " *" : "")
                            }
                            fullWidth
                            error={!!errors.password}
                            helperText={errors.password?.message as string}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: watch("password") ? (
                                <InputAdornment
                                  position="end"
                                  className="cursor-pointer"
                                >
                                  {watch("showPassword") ? (
                                    <VisibilityIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue("showPassword", false)
                                      }
                                    />
                                  ) : (
                                    <VisibilityOffIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue("showPassword", true)
                                      }
                                    />
                                  )}
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type={
                              watch("showConfirmPassword") ? "text" : "password"
                            }
                            {...register("confirmPassword", {
                              required: watch("loginAccess")
                                ? t("This field is required.")
                                : false,
                              validate:
                                watch("loginAccess") ||
                                watch("password")?.length ||
                                watch("confirmPassword")?.length
                                  ? {
                                      passwordMatch: (value) =>
                                        value === watch("password") ||
                                        t("confirmPasswordNewPassword"),
                                    }
                                  : undefined,
                            })}
                            label={
                              t("confirmNewPassword") +
                              (watch("loginAccess") ? " *" : "")
                            }
                            placeholder={t("phReenterPassword")}
                            title={
                              t("confirmNewPassword") +
                              (watch("loginAccess") ? " *" : "")
                            }
                            fullWidth
                            error={!!errors.confirmPassword}
                            helperText={
                              errors.confirmPassword?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: watch("confirmPassword") ? (
                                <InputAdornment
                                  position="end"
                                  className="cursor-pointer"
                                >
                                  {watch("showConfirmPassword") ? (
                                    <VisibilityIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue("showConfirmPassword", false)
                                      }
                                    />
                                  ) : (
                                    <VisibilityOffIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue("showConfirmPassword", true)
                                      }
                                    />
                                  )}
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                ) : null}
                {updateModal && (
                  <FormDialog
                    open={updateModal}
                    onClose={(event: any, reason: any) => {
                      if (event && reason !== "backdropClick") {
                        setUpdateModal(false);
                        updateCustomer({ ...customer, updateImpact: false });
                      }
                    }}
                    hideClose={true}
                    title={t("applyChangesToJCWO")}
                    content={<Typography>{t(`wantToApplyChanges`)}</Typography>}
                    saveFunction={() => {
                      setUpdateModal(false);
                      updateCustomer({ ...customer, updateImpact: true });
                    }}
                    submitButtonText="yes"
                    cancelBtnText="no"
                  />
                )}
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
